import { useEffect, useState } from "react";
import { FileText, Check, AlertTriangle, ShieldCheck, ArrowRight, X } from "lucide-react";

// Section 2 — "How Meshly works." One scenario across five scenes:
// Year 1 ACV was $96,000. The renewal contract is $120,000 ACV. CRM + billing
// reflect the renewal; the ledger revenue schedule rolled forward the Year 1
// ACV → revenue understated by $24,000. Translation → reconciliation → the
// drift peak → investigation → an approved, provable correction.
const MEANING = ["Customer", "Pricing", "Payment terms", "Usage", "Revenue schedule", "Renewal"];
const STAGES = ["Understand", "Compare", "Detect drift", "Explain", "Resolve"];
const INTERVAL = 4200;

function SceneLabel({ tone, children }: { tone: "primary" | "red" | "emerald"; children: React.ReactNode }) {
  const text = tone === "red" ? "text-red-600" : tone === "emerald" ? "text-emerald-600" : "text-primary";
  const dot = tone === "red" ? "bg-red-500" : tone === "emerald" ? "bg-emerald-500" : "bg-primary";
  return (
    <span className={`inline-flex items-center gap-2 font-mono-tech text-[11px] font-medium uppercase tracking-[0.18em] ${text}`}>
      <span className={`h-1.5 w-1.5 rounded-full ${dot}`} />
      {children}
    </span>
  );
}

export default function MeshlyLifecycle() {
  const [s, setS] = useState(0);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    if (paused) return;
    const id = window.setTimeout(() => setS((n) => (n + 1) % STAGES.length), s === STAGES.length - 1 ? 5200 : INTERVAL);
    return () => window.clearTimeout(id);
  }, [s, paused]);

  return (
    <div
      className="overflow-hidden rounded-2xl border border-foreground/10 bg-background"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      {/* subject: the renewal contract, unfolded into meaning */}
      <div className="flex flex-col gap-4 border-b border-border bg-secondary/30 px-6 py-5 sm:flex-row sm:items-center">
        <div className="flex items-center gap-3">
          <span className="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-primary text-primary-foreground">
            <FileText className="h-5 w-5" />
          </span>
          <div>
            <p className="text-sm font-semibold text-foreground">Renewal contract</p>
            <p className="font-mono-tech text-[11px] uppercase tracking-wider text-muted-foreground">$120,000 ACV · 12 mo · eff. Jan 1, 2025</p>
          </div>
        </div>
        <div className="flex flex-wrap gap-1.5 sm:ml-auto sm:justify-end">
          {MEANING.map((m) => (
            <span
              key={m}
              className={`rounded-md border px-2.5 py-1 text-xs transition-all duration-300 ${
                m === "Revenue schedule" && s >= 2
                  ? "border-red-400 bg-red-50 font-medium text-red-600"
                  : s >= 1
                    ? "border-primary/25 bg-primary/[0.04] text-primary"
                    : "border-border text-muted-foreground"
              }`}
            >
              {m}
            </span>
          ))}
        </div>
      </div>

      {/* stepper (tabs stay) */}
      <div className="flex overflow-x-auto border-b border-border">
        {STAGES.map((st, i) => (
          <button
            key={st}
            type="button"
            onClick={() => setS(i)}
            className={`relative flex-1 whitespace-nowrap px-4 py-3.5 font-mono-tech text-[11px] uppercase tracking-wider transition-colors ${
              i === s ? (i === 2 ? "text-red-600" : "text-primary") : "text-muted-foreground hover:text-foreground"
            }`}
          >
            0{i + 1} · {st}
            {i === s && (
              <span
                key={s}
                className={`absolute bottom-0 left-0 h-0.5 ${i === 2 ? "bg-red-500" : "bg-primary"}`}
                style={paused ? { width: "100%" } : { animation: `vh-progress ${INTERVAL}ms linear forwards` }}
              />
            )}
          </button>
        ))}
      </div>

      {/* the scene */}
      <div className="min-h-[260px] px-6 py-7">
        {/* Scene 1 — Understand: reads the renewal contract as meaning */}
        {s === 0 && (
          <div key="s0" className="animate-vh-fade">
            <SceneLabel tone="primary">Translation</SceneLabel>
            <div className="mt-5 grid grid-cols-[1fr_auto_1fr] items-center gap-4">
              <pre className="overflow-hidden rounded-lg border border-border bg-secondary/40 p-3 font-mono-tech text-[10.5px] leading-relaxed text-muted-foreground/70">
{`"Renewal Order, effective
January 1, 2025. Annual
Contract Value: $120,000.
Term: twelve (12) months."`}
              </pre>
              <ArrowRight className="h-5 w-5 shrink-0 text-primary" />
              <div className="space-y-1.5">
                {[
                  ["ACV", "$120,000"],
                  ["Term", "12 months"],
                  ["Effective", "Jan 1, 2025"],
                  ["Source", "executed renewal contract"],
                ].map(([k, v], i) => (
                  <div
                    key={k}
                    className="animate-vh-fade rounded-md border border-primary/25 bg-primary/[0.04] px-2.5 py-1.5"
                    style={{ animationDelay: `${0.15 + i * 0.16}s` }}
                  >
                    <span className="font-mono-tech text-[10px] uppercase tracking-wider text-primary">{k}</span>
                    <span className="ml-2 text-sm text-foreground">{v}</span>
                  </div>
                ))}
              </div>
            </div>
            <p className="mt-5 text-sm text-muted-foreground">Meshly reads the renewal contract as business meaning, not just text.</p>
          </div>
        )}

        {/* Scene 2 — Compare: CRM + billing reflect the renewal; the ledger schedule doesn't */}
        {s === 1 && (
          <div key="s1" className="animate-vh-fade">
            <SceneLabel tone="primary">Reconciliation</SceneLabel>
            <p className="mb-3 mt-4 text-sm text-muted-foreground">The renewal contract ACV, checked against every system.</p>
            <div className="space-y-2">
              {[
                { logo: "salesforce", label: "CRM", field: "ACV", val: "$120,000", ok: true },
                { logo: "stripe", label: "Billing", field: "renewal amount", val: "$120,000", ok: true },
                { logo: "quickbooks", label: "Ledger", field: "revenue schedule", val: "$96,000", ok: false },
              ].map((row, i) => (
                <div
                  key={row.logo}
                  className={`animate-vh-fade flex items-center gap-2 rounded-lg border px-3 py-2.5 sm:gap-3 ${
                    row.ok ? "border-border" : "border-red-300 bg-red-50/60"
                  }`}
                  style={{ animationDelay: `${i * 0.22}s` }}
                >
                  <img src={`/logos/${row.logo}.svg`} alt={row.label} className="h-4 w-4 shrink-0" />
                  <span className="w-14 shrink-0 font-mono-tech text-[11px] uppercase tracking-wider text-muted-foreground">{row.label}</span>
                  <span className="font-mono-tech text-[10px] uppercase tracking-wider text-muted-foreground/70">{row.field}</span>
                  <span className={`tabular-nums text-sm font-medium ${row.ok ? "text-foreground" : "text-red-700"}`}>{row.val}</span>
                  {row.ok ? (
                    <span className="ml-auto inline-flex items-center gap-1.5 text-emerald-600">
                      <Check className="h-3.5 w-3.5" strokeWidth={3} />
                      <span className="font-mono-tech text-[10px] uppercase tracking-wider">matches renewal</span>
                    </span>
                  ) : (
                    <span className="ml-auto inline-flex items-center gap-1.5 text-red-600">
                      <X className="h-3.5 w-3.5" strokeWidth={3} />
                      <span className="font-mono-tech text-[10px] uppercase tracking-wider">still Year 1 ACV</span>
                    </span>
                  )}
                </div>
              ))}
            </div>
            <p className="mt-4 text-sm text-muted-foreground">CRM and billing reflect the renewal contract. The ledger revenue schedule is still on the Year 1 ACV.</p>
          </div>
        )}

        {/* Scene 3 — Detect drift: the peak. the ledger schedule rolled forward Year 1 ACV */}
        {s === 2 && (
          <div key="s2" className="animate-vh-fade">
            <SceneLabel tone="red">Drift detected</SceneLabel>
            <p className="mt-4 text-base font-semibold text-foreground">Revenue schedule rolled forward Year 1 ACV.</p>
            <div className="mt-4 overflow-hidden rounded-xl border-2 border-red-400 shadow-[0_0_0_5px_rgba(239,68,68,0.12)]">
              <div className="flex items-center justify-between border-b border-red-100 bg-background px-4 py-2.5">
                <span className="text-sm text-muted-foreground">
                  Renewal contract ACV <span className="text-muted-foreground/60">· should be scheduled</span>
                </span>
                <span className="tabular-nums text-sm font-medium text-foreground">$120,000</span>
              </div>
              <div className="flex items-center justify-between border-b border-red-100 bg-red-50 px-4 py-2.5">
                <span className="text-sm text-red-700">
                  Ledger scheduled <span className="text-red-500">· carried forward Year 1 ACV</span>
                </span>
                <span className="tabular-nums text-sm font-semibold text-red-700">$96,000</span>
              </div>
              <div className="flex items-center justify-between bg-red-500 px-4 py-3 text-white">
                <span className="inline-flex items-center gap-2 font-mono-tech text-[11px] uppercase tracking-wider">
                  <AlertTriangle className="h-4 w-4" /> Difference
                </span>
                <span className="tabular-nums text-xl font-bold">−$24,000</span>
              </div>
            </div>
            <p className="mt-4 text-sm font-medium text-red-600">Revenue understated by $24,000.</p>
          </div>
        )}

        {/* Scene 4 — Explain: the cause, with evidence, revealed progressively */}
        {s === 3 && (
          <div key="s3" className="animate-vh-fade">
            <SceneLabel tone="primary">Investigation</SceneLabel>
            <div className="mt-4 divide-y divide-border rounded-lg border border-border">
              {[
                ["finding", "The ledger revenue schedule used the Year 1 ACV ($96,000) instead of the renewal contract ACV ($120,000)."],
                ["evidence", "Year 1 ACV: $96,000. Executed renewal contract: $120,000 ACV."],
                ["source", "Renewal contract + CRM + billing + ledger revenue schedule."],
                ["impact", "Revenue understated by $24,000 (the $120,000 renewal minus the $96,000 Year 1 ACV)."],
                ["recommendation", "Regenerate the revenue schedule using the renewal contract ACV."],
              ].map(([k, v], i) => (
                <div
                  key={k}
                  className="animate-vh-fade flex gap-3 px-3.5 py-2.5"
                  style={{ animationDelay: `${i * 0.2}s` }}
                >
                  <span className="w-28 shrink-0 pt-0.5 font-mono-tech text-[10px] uppercase tracking-wider text-primary">{k}</span>
                  <span className="text-sm text-foreground">{v}</span>
                </div>
              ))}
            </div>
            <p className="mt-3 text-sm text-muted-foreground">Meshly explains the cause, traced back to the source.</p>
          </div>
        )}

        {/* Scene 5 — Resolve: the approved, provable correction */}
        {s === 4 && (
          <div key="s4" className="animate-vh-fade">
            <div className="flex flex-col items-center text-center">
              <div className="vh-insert grid h-14 w-14 place-items-center rounded-full bg-emerald-500 text-white shadow-[0_0_0_6px_rgba(16,185,129,0.12)]">
                <Check className="h-7 w-7" strokeWidth={3} />
              </div>
              <p className="mt-4 text-lg font-semibold text-foreground">
                One trusted number. One complete record.
              </p>
              <p className="mt-1 text-sm text-muted-foreground">
                Revenue schedule corrected to the renewal ACV of <span className="tabular-nums">$120,000</span>.
              </p>
            </div>
            <div className="mx-auto mt-5 max-w-md space-y-1.5">
              {[
                "Reviewed and approved by a person or an agent before posting.",
                "Every decision is connected to its evidence, from the renewal contract to the posted number.",
              ].map((line, i) => (
                <div
                  key={line}
                  className="animate-vh-fade flex items-center gap-2 text-sm text-foreground"
                  style={{ animationDelay: `${0.2 + i * 0.12}s` }}
                >
                  <Check className="h-3.5 w-3.5 shrink-0 text-emerald-600" strokeWidth={3} />
                  {line}
                </div>
              ))}
            </div>
            <div className="mt-5 flex flex-wrap justify-center gap-2">
              {["replayable", "traceable", "audit-ready"].map((b, i) => (
                <span
                  key={b}
                  className="animate-vh-fade inline-flex items-center gap-1.5 rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1.5 font-mono-tech text-[11px] uppercase tracking-wider text-emerald-700"
                  style={{ animationDelay: `${0.45 + i * 0.1}s` }}
                >
                  <ShieldCheck className="h-3.5 w-3.5" />
                  {b}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
